import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult } from '@synergy/api';
import { Dispatch } from 'redux';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { ContactDocument } from '../models/contact-detail-document';

export const CONTACT_DETAIL_DOCUMENTS_FETCH = 'CONTACT_DETAIL_DOCUMENTS_FETCH';
export const CONTACT_DETAIL_DOCUMENTS_RECEIVED = 'CONTACT_DETAIL_DOCUMENTS_RECEIVED';
export const CONTACT_DETAIL_DOCUMENTS_RENAME = 'CONTACT_DETAIL_DOCUMENTS_RENAME';
export const CONTACT_DETAIL_DOCUMENTS_DELETE = 'CONTACT_DETAIL_DOCUMENTS_DELETE';

export const CONTACT_DETAIL_DOCUMENTS_UPLOAD_START = 'CONTACT_DETAIL_DOCUMENTS_UPLOAD_START';
export const CONTACT_DETAIL_DOCUMENTS_UPLOAD_PROGRESS = 'CONTACT_DETAIL_DOCUMENTS_UPLOAD_PROGRESS';
export const CONTACT_DETAIL_DOCUMENTS_UPLOAD_SUCCESS = 'CONTACT_DETAIL_DOCUMENTS_UPLOAD_SUCCESS';
export const CONTACT_DETAIL_DOCUMENTS_UPLOAD_FAILURE = 'CONTACT_DETAIL_DOCUMENTS_UPLOAD_FAIL';
export const CONTACT_DETAIL_DOCUMENTS_UPLOAD_CANCEL = 'CONTACT_DETAIL_DOCUMENTS_UPLOAD_CANCEL';

const receiveDocuments = (contactDocuments: ContactDocument[]) => ({
    type: CONTACT_DETAIL_DOCUMENTS_RECEIVED,
    contactDocuments,
});

const uploadDocumentStart = (document: ContactDocument) => {
    return {
        type: CONTACT_DETAIL_DOCUMENTS_UPLOAD_START,
        document,
    };
};

const uploadDocumentProgress = (id: number, progress: number) => {
    return {
        type: CONTACT_DETAIL_DOCUMENTS_UPLOAD_PROGRESS,
        id,
        progress,
    };
};

const uploadDocumentCancel = (document: ContactDocument) => {
    return {
        type: CONTACT_DETAIL_DOCUMENTS_UPLOAD_CANCEL,
        document,
    };
};

const uploadDocumentError = (document: ContactDocument) => {
    return {
        type: CONTACT_DETAIL_DOCUMENTS_UPLOAD_FAILURE,
        document,
    };
};

const uploadDocumentSuccess = (id: number, document: ContactDocument) => {
    return {
        type: CONTACT_DETAIL_DOCUMENTS_UPLOAD_SUCCESS,
        id,
        document,
    };
};

export const fetchContactDocuments = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_DOCUMENTS_FETCH });

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Documents.getDocuments(id);
        if (response.success) {
            dispatch(receiveDocuments(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

interface UploadInfo {
    file: File;
    contactDocument: ContactDocument;
    response?: ApiResult<ContactDocument>;
}

export const addContactDocuments = (files: File[], titles: string[] = []) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return;
        }

        const idBase = new Date().getTime() * -1;
        const uploadInfos = files.map<UploadInfo>((file, index) => ({
            file,
            contactDocument: {
                id: idBase - index,
                size: file.size,
                title: file.name,
                filesystem: '',
                uploadProgress: 0,
                isVisibleInShop: false,
            },
        }));
        uploadInfos.forEach(info => dispatch(uploadDocumentStart(info.contactDocument)));

        const uploadingDocumentPromises = uploadInfos.map(info =>
            api.contactsAndOrganizations.Contact.ContactDetail.Documents.addDocument(
                contactId,
                info.file,
                c => (info.contactDocument.cancel = c),
                progress => {
                    const percentCompleted = Math.floor((progress.loaded * 100) / progress.total);
                    dispatch(uploadDocumentProgress(info.contactDocument.id, percentCompleted));
                }
            )
        );

        const responses = await Promise.all(uploadingDocumentPromises);
        responses.forEach((response, index) => (uploadInfos[index].response = response));

        // The same notice the documents give: stored the moment it is dropped (R-197).
        const saved = uploadInfos.filter(info => info.response?.success).length;
        if (saved > 0) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.upload.saved', { count: saved })));
        }

        uploadInfos.forEach((uploadInfo, index) => {
            if (uploadInfo.response?.success) {
                const document = uploadInfo.response.result;
                dispatch(uploadDocumentSuccess(uploadInfo.contactDocument.id, document));

                // The upload takes the file only; a title given with it follows.
                const title = titles[index]?.trim();
                if (title && title !== document.title) {
                    renameDocument(document.id, title)(dispatch, getState, { api });
                }
            } else {
                if (uploadInfo.response?.error instanceof Error) {
                    dispatch(uploadDocumentError(uploadInfo.contactDocument));
                } else {
                    dispatch(uploadDocumentCancel(uploadInfo.contactDocument));
                }
            }
        });
    };
};

export const renameDocument = (id: number, title: string) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        const contactDocument = getState().contactsAndOrganizations.contacts.contactDetail.documents.documents.find(
            document => document.id === id
        );
        if (!contactId || !contactDocument) {
            return;
        }

        dispatch({ type: CONTACT_DETAIL_DOCUMENTS_RENAME, id, title });

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Documents.updateDocument(contactId, {
            id: contactDocument.id,
            title,
        });

        if (response.success) {
            dispatch(
                Snackbar.enqueue(PoolNotification.success('app.contactsAndOrganizations.documents.editTitleSuccess'))
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteDocument = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return;
        }

        dispatch({ type: CONTACT_DETAIL_DOCUMENTS_DELETE, id });

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Documents.deleteDocument(
            contactId,
            id
        );

        if (response.success) {
            dispatch(
                Snackbar.enqueue(PoolNotification.success('app.contactsAndOrganizations.documents.deleteSuccess'))
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
